/**
 * Procedural silhouette renderer. Until Lucas commissions real art, characters
 * are rendered as filled black polygons in distinct shapes so each type is
 * readable at a glance. Each silhouette exposes a `handAnchor` so the
 * procedurally-rendered weapon can be placed in its grip.
 */

export interface SilhouetteAnchor {
  /** Offset from silhouette draw origin, in logical px. */
  x: number;
  y: number;
  /** Default weapon rotation in radians (0 = blade points right). */
  rotation: number;
}

export interface SilhouetteOptions {
  /** Logical-coord center for the silhouette's feet. */
  cx: number;
  feetY: number;
  /** Horizontal flip — true = facing left (mirrors weapon anchor too). */
  flip?: boolean;
  /** Subtle idle-sway phase in radians (added to sin for breathing motion). */
  swayPhase?: number;
  /** Vertical bob (e.g., during a knockback). Positive = pushed back. */
  recoil?: number;
  /** Tilt in radians applied to the whole silhouette. */
  tilt?: number;
  /** Tint alpha 0-1 (defaults to 1, near-black). */
  tint?: string;
  /** Renders a faded "stumble" version for the kill-window state. */
  stumble?: boolean;
}

export type SilhouetteId =
  | 'knight'
  | 'ranger'
  | 'rogue'
  | 'goblin'
  | 'wolf'
  | 'bandit'
  | 'ogre'
  | 'wraith';

const HEIGHT_BY_ID: Record<SilhouetteId, number> = {
  knight: 130,
  ranger: 128,
  rogue: 124,
  goblin: 100,
  wolf: 78,
  bandit: 126,
  ogre: 158,
  wraith: 134,
};

/** Returns the anchor where the weapon should be drawn for this silhouette. */
export function handAnchor(id: SilhouetteId): SilhouetteAnchor {
  switch (id) {
    case 'knight':
      return { x: 22, y: -68, rotation: -0.2 };
    case 'ranger':
      return { x: 20, y: -64, rotation: -0.15 };
    case 'rogue':
      return { x: 18, y: -60, rotation: -0.1 };
    case 'bandit':
      return { x: 22, y: -64, rotation: -0.05 };
    default:
      return { x: 0, y: -40, rotation: 0 };
  }
}

export function heroHeight(id: SilhouetteId): number {
  return HEIGHT_BY_ID[id];
}

interface DrawCtx {
  ctx: CanvasRenderingContext2D;
  cx: number;
  feetY: number;
  flip: boolean;
  swayPhase: number;
  recoil: number;
  tilt: number;
  tint: string;
  stumble: boolean;
}

export function drawSilhouette(
  ctx: CanvasRenderingContext2D,
  id: SilhouetteId,
  opts: SilhouetteOptions,
): void {
  const {
    cx,
    feetY,
    flip = false,
    swayPhase = 0,
    recoil = 0,
    tilt = 0,
    tint = '#0a0805',
    stumble = false,
  } = opts;
  const d: DrawCtx = {
    ctx,
    cx,
    feetY,
    flip,
    swayPhase,
    recoil,
    tilt,
    tint,
    stumble,
  };
  ctx.save();
  ctx.translate(cx + (flip ? -recoil : recoil), feetY);
  if (flip) ctx.scale(-1, 1);
  if (tilt !== 0) {
    ctx.translate(0, -HEIGHT_BY_ID[id] * 0.4);
    ctx.rotate(tilt);
    ctx.translate(0, HEIGHT_BY_ID[id] * 0.4);
  }
  ctx.fillStyle = tint;

  switch (id) {
    case 'knight':
      drawKnight(d);
      break;
    case 'ranger':
      drawRanger(d);
      break;
    case 'rogue':
      drawRogue(d);
      break;
    case 'goblin':
      drawGoblin(d);
      break;
    case 'wolf':
      drawWolf(d);
      break;
    case 'bandit':
      drawBandit(d);
      break;
    case 'ogre':
      drawOgre(d);
      break;
    case 'wraith':
      drawWraith(d);
      break;
  }
  ctx.restore();
}

// All silhouettes draw with feet at (0,0) in their own local frame, head up at -height.

function drawKnight(d: DrawCtx): void {
  const { ctx, swayPhase } = d;
  const sway = Math.sin(swayPhase) * 1.2;
  ctx.beginPath();
  // Right boot
  ctx.moveTo(-12, 0);
  ctx.lineTo(-3, 0);
  ctx.lineTo(-2, -28);
  ctx.lineTo(-10, -34);
  ctx.closePath();
  ctx.fill();
  // Left boot
  ctx.beginPath();
  ctx.moveTo(3, 0);
  ctx.lineTo(12, 0);
  ctx.lineTo(11, -32);
  ctx.lineTo(2, -28);
  ctx.closePath();
  ctx.fill();
  // Body / torso + cape line
  ctx.beginPath();
  ctx.moveTo(-14, -30);
  ctx.lineTo(-16, -54);
  ctx.lineTo(-18, -80);
  ctx.lineTo(-12, -98);
  ctx.lineTo(-6, -106);
  ctx.lineTo(6 + sway, -106);
  ctx.lineTo(12, -98);
  ctx.lineTo(18, -82);
  ctx.lineTo(16, -54);
  ctx.lineTo(14, -30);
  ctx.closePath();
  ctx.fill();
  // Head with helm crest
  ctx.beginPath();
  ctx.ellipse(0, -116, 11, 13, 0, 0, Math.PI * 2);
  ctx.fill();
  // Helm crest
  ctx.beginPath();
  ctx.moveTo(-6, -126);
  ctx.lineTo(2, -132);
  ctx.lineTo(8, -126);
  ctx.lineTo(0, -124);
  ctx.closePath();
  ctx.fill();
  // Arm (sword side)
  ctx.beginPath();
  ctx.moveTo(8, -86);
  ctx.lineTo(20, -78);
  ctx.lineTo(26, -68);
  ctx.lineTo(22, -62);
  ctx.lineTo(14, -76);
  ctx.lineTo(6, -80);
  ctx.closePath();
  ctx.fill();
  // Off-arm
  ctx.beginPath();
  ctx.moveTo(-8, -86);
  ctx.lineTo(-18, -76);
  ctx.lineTo(-20, -60);
  ctx.lineTo(-14, -58);
  ctx.lineTo(-12, -70);
  ctx.lineTo(-6, -80);
  ctx.closePath();
  ctx.fill();
}

function drawRanger(d: DrawCtx): void {
  const { ctx, swayPhase } = d;
  const sway = Math.sin(swayPhase) * 1;
  // Legs (lean stance)
  ctx.beginPath();
  ctx.moveTo(-10, 0);
  ctx.lineTo(-2, 0);
  ctx.lineTo(-3, -34);
  ctx.lineTo(-9, -36);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(2, 0);
  ctx.lineTo(10, 0);
  ctx.lineTo(8, -36);
  ctx.lineTo(2, -34);
  ctx.closePath();
  ctx.fill();
  // Torso (hooded)
  ctx.beginPath();
  ctx.moveTo(-12, -32);
  ctx.lineTo(-14, -68);
  ctx.lineTo(-10, -92);
  ctx.lineTo(0 + sway, -100);
  ctx.lineTo(10, -92);
  ctx.lineTo(14, -68);
  ctx.lineTo(12, -32);
  ctx.closePath();
  ctx.fill();
  // Hood
  ctx.beginPath();
  ctx.moveTo(-14, -94);
  ctx.lineTo(-8, -116);
  ctx.lineTo(8, -116);
  ctx.lineTo(14, -94);
  ctx.lineTo(8, -100);
  ctx.lineTo(-8, -100);
  ctx.closePath();
  ctx.fill();
  // Head
  ctx.beginPath();
  ctx.ellipse(0, -108, 8, 10, 0, 0, Math.PI * 2);
  ctx.fill();
  // Quiver shape behind
  ctx.beginPath();
  ctx.moveTo(-16, -64);
  ctx.lineTo(-20, -90);
  ctx.lineTo(-16, -90);
  ctx.lineTo(-13, -64);
  ctx.closePath();
  ctx.fill();
  // Hand on weapon
  ctx.beginPath();
  ctx.moveTo(8, -78);
  ctx.lineTo(20, -68);
  ctx.lineTo(22, -60);
  ctx.lineTo(14, -60);
  ctx.lineTo(6, -72);
  ctx.closePath();
  ctx.fill();
}

function drawRogue(d: DrawCtx): void {
  const { ctx, swayPhase } = d;
  const sway = Math.sin(swayPhase) * 0.8;
  // Slim figure, crouched stance
  ctx.beginPath();
  ctx.moveTo(-9, 0);
  ctx.lineTo(-2, 0);
  ctx.lineTo(-1, -30);
  ctx.lineTo(-8, -32);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(2, 0);
  ctx.lineTo(9, 0);
  ctx.lineTo(8, -32);
  ctx.lineTo(2, -30);
  ctx.closePath();
  ctx.fill();
  // Torso with cloak
  ctx.beginPath();
  ctx.moveTo(-10, -28);
  ctx.lineTo(-14, -56);
  ctx.lineTo(-10, -84);
  ctx.lineTo(-2 + sway, -94);
  ctx.lineTo(10, -84);
  ctx.lineTo(14, -56);
  ctx.lineTo(10, -28);
  ctx.closePath();
  ctx.fill();
  // Head, masked
  ctx.beginPath();
  ctx.ellipse(0, -102, 7, 9, 0, 0, Math.PI * 2);
  ctx.fill();
  // Cloak flare
  ctx.beginPath();
  ctx.moveTo(-12, -56);
  ctx.lineTo(-22, -34);
  ctx.lineTo(-18, -30);
  ctx.lineTo(-10, -52);
  ctx.closePath();
  ctx.fill();
  // Sword arm extended
  ctx.beginPath();
  ctx.moveTo(8, -78);
  ctx.lineTo(18, -64);
  ctx.lineTo(20, -56);
  ctx.lineTo(12, -56);
  ctx.lineTo(6, -68);
  ctx.closePath();
  ctx.fill();
}

function drawGoblin(d: DrawCtx): void {
  const { ctx, swayPhase, stumble } = d;
  const sway = Math.sin(swayPhase) * 1.4;
  // Hunched body, short legs
  ctx.beginPath();
  ctx.moveTo(-8, 0);
  ctx.lineTo(-2, 0);
  ctx.lineTo(-2, -22);
  ctx.lineTo(-8, -24);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(2, 0);
  ctx.lineTo(8, 0);
  ctx.lineTo(8, -24);
  ctx.lineTo(2, -22);
  ctx.closePath();
  ctx.fill();
  // Hunched torso
  ctx.beginPath();
  ctx.moveTo(-14, -22);
  ctx.lineTo(-18, -50);
  ctx.lineTo(-10, -72 + sway);
  ctx.lineTo(8, -72 + sway);
  ctx.lineTo(16, -50);
  ctx.lineTo(12, -22);
  ctx.closePath();
  ctx.fill();
  // Pointy-eared head
  ctx.beginPath();
  ctx.moveTo(-6, -74);
  ctx.lineTo(-14, -88);
  ctx.lineTo(-4, -84);
  ctx.lineTo(2, -94);
  ctx.lineTo(8, -84);
  ctx.lineTo(14, -88);
  ctx.lineTo(8, -76);
  ctx.closePath();
  ctx.fill();
  // Arm with crude blade
  if (!stumble) {
    ctx.beginPath();
    ctx.moveTo(-16, -56);
    ctx.lineTo(-26, -50);
    ctx.lineTo(-28, -42);
    ctx.lineTo(-20, -44);
    ctx.lineTo(-12, -52);
    ctx.closePath();
    ctx.fill();
  }
}

function drawWolf(d: DrawCtx): void {
  const { ctx } = d;
  // 4-legged, low to ground
  // Back legs
  ctx.beginPath();
  ctx.moveTo(-30, 0);
  ctx.lineTo(-20, 0);
  ctx.lineTo(-18, -24);
  ctx.lineTo(-30, -22);
  ctx.closePath();
  ctx.fill();
  // Front legs
  ctx.beginPath();
  ctx.moveTo(18, 0);
  ctx.lineTo(28, 0);
  ctx.lineTo(28, -22);
  ctx.lineTo(18, -24);
  ctx.closePath();
  ctx.fill();
  // Body (elongated)
  ctx.beginPath();
  ctx.moveTo(-32, -24);
  ctx.lineTo(-28, -44);
  ctx.lineTo(-10, -56);
  ctx.lineTo(20, -54);
  ctx.lineTo(32, -38);
  ctx.lineTo(28, -22);
  ctx.lineTo(-22, -22);
  ctx.closePath();
  ctx.fill();
  // Head and snout
  ctx.beginPath();
  ctx.moveTo(20, -56);
  ctx.lineTo(38, -54);
  ctx.lineTo(46, -46);
  ctx.lineTo(40, -42);
  ctx.lineTo(34, -44);
  ctx.lineTo(26, -42);
  ctx.closePath();
  ctx.fill();
  // Ears
  ctx.beginPath();
  ctx.moveTo(24, -56);
  ctx.lineTo(20, -68);
  ctx.lineTo(28, -60);
  ctx.closePath();
  ctx.fill();
  // Tail
  ctx.beginPath();
  ctx.moveTo(-30, -32);
  ctx.lineTo(-46, -26);
  ctx.lineTo(-48, -16);
  ctx.lineTo(-36, -24);
  ctx.closePath();
  ctx.fill();
}

function drawBandit(d: DrawCtx): void {
  const { ctx, swayPhase } = d;
  const sway = Math.sin(swayPhase) * 1;
  // Wider stance
  ctx.beginPath();
  ctx.moveTo(-14, 0);
  ctx.lineTo(-4, 0);
  ctx.lineTo(-4, -30);
  ctx.lineTo(-12, -34);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(4, 0);
  ctx.lineTo(14, 0);
  ctx.lineTo(12, -34);
  ctx.lineTo(4, -30);
  ctx.closePath();
  ctx.fill();
  // Bulky torso
  ctx.beginPath();
  ctx.moveTo(-14, -30);
  ctx.lineTo(-20, -60);
  ctx.lineTo(-16, -90);
  ctx.lineTo(-4 + sway, -100);
  ctx.lineTo(8, -100);
  ctx.lineTo(18, -90);
  ctx.lineTo(20, -60);
  ctx.lineTo(14, -30);
  ctx.closePath();
  ctx.fill();
  // Head with scarf
  ctx.beginPath();
  ctx.ellipse(2, -110, 9, 11, 0, 0, Math.PI * 2);
  ctx.fill();
  // Scarf
  ctx.beginPath();
  ctx.moveTo(-8, -100);
  ctx.lineTo(-14, -94);
  ctx.lineTo(-10, -86);
  ctx.lineTo(-4, -90);
  ctx.closePath();
  ctx.fill();
  // Weapon arm
  ctx.beginPath();
  ctx.moveTo(10, -82);
  ctx.lineTo(22, -76);
  ctx.lineTo(26, -64);
  ctx.lineTo(18, -62);
  ctx.lineTo(12, -72);
  ctx.closePath();
  ctx.fill();
}

function drawOgre(d: DrawCtx): void {
  const { ctx, swayPhase } = d;
  const sway = Math.sin(swayPhase) * 1.6;
  // Massive legs
  ctx.beginPath();
  ctx.moveTo(-22, 0);
  ctx.lineTo(-6, 0);
  ctx.lineTo(-4, -44);
  ctx.lineTo(-18, -48);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(6, 0);
  ctx.lineTo(22, 0);
  ctx.lineTo(18, -48);
  ctx.lineTo(4, -44);
  ctx.closePath();
  ctx.fill();
  // Belly + chest
  ctx.beginPath();
  ctx.moveTo(-26, -44);
  ctx.lineTo(-34, -90);
  ctx.lineTo(-26, -118);
  ctx.lineTo(-10 + sway, -130);
  ctx.lineTo(14, -130);
  ctx.lineTo(30, -118);
  ctx.lineTo(34, -90);
  ctx.lineTo(26, -44);
  ctx.closePath();
  ctx.fill();
  // Small head, sunken between shoulders
  ctx.beginPath();
  ctx.ellipse(4, -136, 11, 13, 0, 0, Math.PI * 2);
  ctx.fill();
  // Tusks
  ctx.beginPath();
  ctx.moveTo(-2, -130);
  ctx.lineTo(-6, -122);
  ctx.lineTo(-2, -124);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(10, -130);
  ctx.lineTo(14, -122);
  ctx.lineTo(10, -124);
  ctx.closePath();
  ctx.fill();
  // Massive club arm
  ctx.beginPath();
  ctx.moveTo(28, -100);
  ctx.lineTo(46, -80);
  ctx.lineTo(50, -56);
  ctx.lineTo(38, -52);
  ctx.lineTo(32, -78);
  ctx.lineTo(24, -90);
  ctx.closePath();
  ctx.fill();
  // Off arm
  ctx.beginPath();
  ctx.moveTo(-30, -100);
  ctx.lineTo(-44, -82);
  ctx.lineTo(-44, -62);
  ctx.lineTo(-36, -60);
  ctx.lineTo(-32, -78);
  ctx.lineTo(-26, -90);
  ctx.closePath();
  ctx.fill();
}

function drawWraith(d: DrawCtx): void {
  const { ctx, swayPhase } = d;
  const sway = Math.sin(swayPhase * 0.6) * 3;
  // No feet — taper into mist near the ground
  ctx.beginPath();
  ctx.moveTo(-22, 0);
  ctx.lineTo(-8, -8);
  ctx.lineTo(8, -8);
  ctx.lineTo(22, 0);
  ctx.lineTo(20, -2);
  ctx.lineTo(-20, -2);
  ctx.closePath();
  ctx.fill();
  // Tattered robe body
  ctx.beginPath();
  ctx.moveTo(-26, -10);
  ctx.lineTo(-30 + sway, -50);
  ctx.lineTo(-22, -84);
  ctx.lineTo(-10, -100);
  ctx.lineTo(10, -100);
  ctx.lineTo(22, -84);
  ctx.lineTo(30 + sway, -50);
  ctx.lineTo(26, -10);
  ctx.lineTo(14, -22);
  ctx.lineTo(0, -16);
  ctx.lineTo(-14, -22);
  ctx.closePath();
  ctx.fill();
  // Hood
  ctx.beginPath();
  ctx.moveTo(-16, -98);
  ctx.lineTo(-14, -124);
  ctx.lineTo(0, -134);
  ctx.lineTo(14, -124);
  ctx.lineTo(16, -98);
  ctx.lineTo(10, -104);
  ctx.lineTo(-10, -104);
  ctx.closePath();
  ctx.fill();
  // Eye glow voids (subtle — drawn as transparent gaps in the silhouette)
  // Approximated by drawing two tiny dim grey rectangles inside the hood.
  ctx.fillStyle = '#5a3030';
  ctx.fillRect(-6, -120, 3, 3);
  ctx.fillRect(3, -120, 3, 3);
  ctx.fillStyle = '#0a0805';
  // Long claw arm
  ctx.beginPath();
  ctx.moveTo(18, -84);
  ctx.lineTo(32, -68);
  ctx.lineTo(36, -54);
  ctx.lineTo(40, -48);
  ctx.lineTo(34, -46);
  ctx.lineTo(28, -54);
  ctx.lineTo(20, -72);
  ctx.closePath();
  ctx.fill();
}

/** Returns the screen-space hand anchor for the silhouette, accounting for flip. */
export function worldHandAnchor(
  id: SilhouetteId,
  cx: number,
  feetY: number,
  flip: boolean,
): { x: number; y: number; rotation: number } {
  const a = handAnchor(id);
  const x = flip ? cx - a.x : cx + a.x;
  return { x, y: feetY + a.y, rotation: flip ? Math.PI - a.rotation : a.rotation };
}
